import { SessionStorage } from '@witkac/kartamiejska-shared';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { ApolloClient } from 'apollo-client';
import { ApolloLink, from, Observable } from 'apollo-link';
import { HttpLink } from 'apollo-link-http';
import Vue from 'vue';
import VueApollo from 'vue-apollo';

import router from './router';
import store from './store/store';
import { config } from './utils/config';
import { UNAUTHORIZED_MESSAGES } from './utils/constants';

Vue.use(VueApollo);

const httpLink = new HttpLink({ uri: config.graphQLURL });

const tokenApplyMiddleware = new ApolloLink((operation, forward) => {
  if (forward) {
    const token = localStorage.getItem(SessionStorage.TOKEN);
    if (token) {
      operation.setContext({
        headers: {
          authorization: `Bearer ${token}`
        }
      });
    }
    return forward(operation);
  }
  return null;
});

const tokenRefreshMiddleware = new ApolloLink((operation, forward) => {
  if (forward) {
    return forward(operation).map(response => {
      const context = operation.getContext();
      const headers = context.response.headers;
      if (headers) {
        const newToken = headers.get('token');
        if (newToken) {
          localStorage.setItem(SessionStorage.TOKEN, newToken);
        }
      }

      return response;
    });
  }
  return null;
});

const isUnauthorizedError = (err: any): boolean => {
  const code = err?.extensions?.code;
  return code === 'UNAUTHENTICATED' || code === 'FORBIDDEN' || UNAUTHORIZED_MESSAGES.includes(err?.message);
};

const forceLogout = (): void => {
  store.commit('auth/unauthorize');
  store.commit('access/reset');
  if (router.currentRoute?.name !== 'login') {
    router.push({ name: 'login' });
  }
};

const unauthorizedMiddleware = new ApolloLink((operation, forward) => {
  if (forward) {
    return new Observable(observer => {
      const subscription = forward(operation).subscribe({
        next: (response: any) => {
          if (response?.errors?.some(isUnauthorizedError)) {
            forceLogout();
          }
          observer.next(response);
        },
        error: (networkError: any) => {
          const status = networkError?.statusCode || networkError?.status || networkError?.response?.status;
          if (status === 401 || status === 403 || networkError?.result?.errors?.some(isUnauthorizedError)) {
            forceLogout();
          }
          observer.error(networkError);
        },
        complete: () => observer.complete()
      });
      return () => subscription.unsubscribe();
    });
  }
  return null;
});

export const apolloClient = new ApolloClient({
  link: from([unauthorizedMiddleware, tokenApplyMiddleware, tokenRefreshMiddleware, httpLink]),
  cache: new InMemoryCache({ resultCaching: false })
});

const apolloProvider = new VueApollo({
  defaultClient: apolloClient
});

export default apolloProvider;
